<script lang="ts" setup>
import { ButtonComp, IconComp, type ButtonTypes } from '@/components';
import type { IIcon, IIconPosition, ITranslationkey } from '@/components/Interfaces';
import { computed, onMounted, unref, watch } from 'vue';
import { ref } from 'vue';
import { Dropdown } from 'bootstrap';
import type { ComponentPublicInstance } from 'vue';
import type { IDropDownExposed } from './IDropDownExposed';
import { DropdownItemButton, DropdownItemMenu, type DropdownItemTypes, DropdownItemGroup, DropdownItemSeparator } from '@/objects';
import DropdownCompItemRenderer from './DropdownCompItemRenderer.vue';
import type { TaskLoaderState } from '@/composeables';
import type { Ref } from 'vue';

const props = withDefaults(
    defineProps<
        {
            showArrow?: boolean;

            /**
             * Sets the autoclose behavior of the dropdown
             *
             * If not explicitly set, it will default to 'outside' if there is a submenu in the dropdownitems (at the creation time of the dropdown instance)
             * and true otherwise.
             * Explicitly set value will always override the default value. (Note that a submenu wont work with autoClose != 'outside' tho)
             *
             * Note: If you have a checkbox inside the dropdown, and you click the checkbox label, the dropdown will close if autoClose is set to true (which is the default)
             * Maybe we should change the default to inside? Basically we only want the true behaviour if this is a select style thing with a bunch of buttons
             */
            autoClose?: 'inside' | 'outside' | true | false;

            /**
             * If the dropdown should use columns
             * @default undefined No columns
             */
            columns?: 1 | 2 | 3 | 4 | 5;

            /**
             * The type of button
             */
            buttonType?: ButtonTypes;

            /**
             * The items in the dropdown
             */
            dropdownItems?: DropdownItemTypes[];

            /**
             * Puts the dropdown into Split mode if supplied.
             * This will be the action for the triggering button.
             * The dropdown items will be in the dropdown menu which can be opened with a split button to the right of the triggering button.
             */
            splitAction?: DropdownItemButton;

            /**
             * Text to show if the dropdown does not have any items
             */
            emptytext?: string;

            /**
             * If the input is disabled
             * @default undefined
             */
            disabled?: boolean;

            /**
             * This will show a loader in the button and disable the button
             * @default null
             */
            loadstate?: TaskLoaderState | null;

            /**
             * if we should show a badge with the number of items in the dropdown
             * @default false
             */
            showItemCountBadge?: boolean;

            /**
             * The maximum items to render at any given time
             * If this is set the dropdown uses a virtual scroller to only render some of the items.
             * A reasonable number to put here if you have alot of items is 25
             */
            virtualScrollerMaxItems?: number;

            /**
             * Internal classes to apply to the dropdown wrapper
             */
            classes?: string;
        } & IIcon &
            IIconPosition &
            ITranslationkey
    >(),
    {
        showArrow: true,
        autoClose: undefined,
        columns: undefined,
        buttonType: 'Secondary',
        showItemCountBadge: false,
    },
);

/**
 * Wrapper div for the whole dropdown including trigger and menu
 */
const dropdownelement = ref<HTMLElement | null>(null);

/**
 * The ul that contains all the menu items (and the virtual scroller)
 */
const dropdownMenuUl = ref<HTMLUListElement | null>(null);

// Bootstrap creates a dropdown instance for the element that has the data-bs-toggle="dropdown" attribute
// So if this element exists, we want to create the dropdown instance for this element
// If it doesnt exist, we want to create the dropdown instance for the dropdown wrapper itself, and the dropdown will be used programatically
// TODO: If we want to put something with a data-bs-toggle inside the trigger slot, we are still doing this wrong. Send the triggering element in as a prop?
// Check the dom at runtime for a data-bs-toggle="dropdown" element?
const dropdownTriggerElement = ref<ComponentPublicInstance | null>(null);

/**
 * Bootstrap dropdownInstance
 */
const dropdownInstance = ref<Dropdown | null>(null);

/**
 * This is to make sure we dont render outside the viewport.
 */
const maxDropdownHeightInPixels = ref<number>(200); // Default to 200 since that seems like a reasonable number. This will be overwritten on show anyway

/**
 * Get the number of adtion items in the dropdown
 */
const computedDropdowItemsCount = computed(() => {
    if (!props.showItemCountBadge) {
        return 0;
    }

    const groupItemCounterFn = (item: DropdownItemTypes): number => {
        if (item instanceof DropdownItemGroup) {
            return item.items.reduce((acc, innerItem) => acc + groupItemCounterFn(innerItem), 0);
        }
        return 1;
    };

    return (
        props.dropdownItems?.reduce((acc, item) => {
            if (item instanceof DropdownItemSeparator) {
                return 0;
            }
            return groupItemCounterFn(item) + acc;
        }, 0) ?? 0
    );
});

onMounted(() => {
    if (!dropdownelement.value) {
        console.warn('Dropdown wrapper is not set by the time we reached onMounted. This should never happen.');
        return;
    }

    const autoClose = props.autoClose ?? (props.dropdownItems?.some((t) => t instanceof DropdownItemMenu) ? 'outside' : true);

    dropdownInstance.value = Dropdown.getOrCreateInstance(dropdownTriggerElement.value?.$el ?? dropdownelement.value, {
        autoClose: autoClose,
        popperConfig: {
            strategy: 'fixed',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 2],
                        boundary: 'window',
                    },
                },
            ],
        },
    });

    dropdownelement.value.addEventListener('resize', () => {
        dropdownInstance.value?.update();
    });

    dropdownelement.value.addEventListener('hidden.bs.dropdown', () => {
        emit('hidden');
    });

    dropdownelement.value.addEventListener('shown.bs.dropdown', () => {
        emit('shown');

        const dropdownMenu = dropdownelement.value;
        if (dropdownMenu instanceof HTMLDivElement) {
            const rect = dropdownMenu.getBoundingClientRect();
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            const spaceBelow = viewportHeight - rect.top - 100; // 100px margin from bottom

            // Set the maxHeight to be the space below the dropdown trigger or 200px minimum
            maxDropdownHeightInPixels.value = spaceBelow < 200 ? 200 : spaceBelow;
        }

        // Reset the scroll position when we open
        // I'm not sure why I have to do this, but if I dont then we start half scrolled for some reason
        if (dropdownMenuUl.value) {
            // Reset scroll position
            dropdownMenuUl.value.scrollTop = 0;
        }

        calculateVirtualScrollerData(false);
    });
});

const show = () => {
    if (dropdownInstance.value) {
        dropdownInstance.value.show();
    }
};

const hide = () => {
    if (dropdownInstance.value) {
        dropdownInstance.value.hide();
    }
};
const toggle = () => {
    if (dropdownInstance.value) {
        dropdownInstance.value.toggle();
    }
};

defineExpose<IDropDownExposed>({
    show,
    hide,
    toggle,
});

const emit = defineEmits<{
    /**
     * Emitted when the dropdown is shown
     */
    (e: 'shown'): void;

    /**
     * Emitted when the dropdown is hidden
     */
    (e: 'hidden'): void;
}>();

const isDropDownItemHidden = (dropdownItem: DropdownItemTypes) => {
    if (dropdownItem.hideDropDownItem === undefined) {
        return false;
    }
    return dropdownItem.hideDropDownItem.value;
};

const dropdownItemsThatShouldNOTHaveDropdownItemClass = [DropdownItemSeparator, DropdownItemMenu];

const visibleDropdownItems = computed(() => {
    const arr = props.dropdownItems?.filter((i) => !isDropDownItemHidden(i)) ?? [];
    if (props.virtualScrollerMaxItems === undefined) {
        return arr;
    }
    return arr.slice(virtualScrollStartIndex.value, virtualScrollEndIndex.value);
});

// Virtual scroller
// We use a virtual scroller so if we have alot of items in the dropdown, we dont render them all at once

/**
 * The div that contains the virtual scroller items
 * This will move to be positioned about where we have scrolled to
 */
const virtualScrollerScrollDiv = ref<HTMLElement | null>(null);

/**
 * How much to move the virtual scroller div
 */
const virtualScrollerScrollTransitionInPixels = ref(0);

/**
 * The first item that is visible in the virtual scroll window
 */
const virtualScrollStartIndex = ref(0);

/**
 * The last item that is visible in the virtual scroll window
 */
const virtualScrollEndIndex = ref(props.virtualScrollerMaxItems ?? 0);

/**
 * A map of item start pixel positions for each item in the dropdown
 * From this we know where to position the virtual scroller div
 * Note: This assumes that any "special" items are at the start.
 * We use the initially rendered items to find the size of each item.
 * For any item that has not been rendered, we assume that is has the same size as last initially rendered.
 * This will make a big problem for periodic separators for example.
 */
const virtualScrollItemStartPixelmap: number[] = [];

/**
 * The pixel height of each item in the dropdown
 * Note: This might not be accurate. It will be accurate for all the items from 0 to virtualScrollerMaxItems, the rest are the same height as the last rendered item.
 */
const virtualScrollItemPixelHeightMap: number[] = [];

/**
 * The total height of all items.
 * This is so we get a scrollbar that is long enough
 */
const virtualScrollerTotalHeight: Ref<number | null> = ref(null);

/**
 * If any of the items change their visibility then we need to recalculate the total height
 */
if (props.virtualScrollerMaxItems !== undefined) {
    if (props.dropdownItems) {
        watch(
            // Watch each hideDropDownItem ref
            () => props.dropdownItems!.filter((di) => di.hideDropDownItem !== undefined).map((di) => di.hideDropDownItem!.value),
            () => {
                if (!props.dropdownItems) {
                    // Should never happen. What are we watching then?
                    return;
                }

                if (virtualScrollItemPixelHeightMap.length !== props.dropdownItems.length) {
                    // We have not calculated the pixelmap yet
                    // We should probably never get here either
                    console.warn(
                        'Dropdown virtual scroller: Pixelmap length does not match dropdown items length. This means that we are changing the visibility before initial calculation has happened (meaning we havent opened the dropdown yet).',
                    );
                    return;
                }

                let newHeight = 0;
                props.dropdownItems.filter((dropdownitem, index) => {
                    if (!isDropDownItemHidden(dropdownitem)) {
                        // Add the height of this item
                        newHeight += virtualScrollItemPixelHeightMap[index];
                    }
                });
                virtualScrollerTotalHeight.value = newHeight;
            },
        );
    }
}

/**
 * Calculate the virtual scroller data
 * This will calculate the start pixel for each item and the total height of all items
 * @param recalculate If we should recalculate even if we already have data
 */
const calculateVirtualScrollerData = (recalculate: boolean) => {
    // We are not in virtualscroller mode so just exit
    if (props.virtualScrollerMaxItems === undefined) {
        return;
    }
    if (!props.dropdownItems) {
        return;
    }
    if (!virtualScrollerScrollDiv.value) {
        return;
    }
    if (virtualScrollItemStartPixelmap.length > 0 && !recalculate) {
        // Guess we already did this
        return;
    }
    let currentPixel = 0;
    let lastElementHeight = 0;
    // Calculate the start pixel for each item
    for (let i = 0; i < props.dropdownItems.length; i++) {
        // Push what we calculated in the previous iteration
        virtualScrollItemStartPixelmap.push(currentPixel);

        // Find the actual html element
        const element = virtualScrollerScrollDiv.value.children.item(i);

        // If no element we are outside of the rendered window and we should use the last known height
        if (!element) {
            currentPixel += lastElementHeight;

            // Maintain the map of the heights
            virtualScrollItemPixelHeightMap.push(lastElementHeight);
            continue;
        }

        // Use offsetHeight if available, otherwise fallback to clientHeight
        if ('offsetHeight' in element && typeof element.offsetHeight === 'number') {
            lastElementHeight = element.offsetHeight;
        } else {
            console.warn('Element does not have offsetHeight property, using clientHeight instead', element);
            lastElementHeight = element.clientHeight;
        }

        // add to currentpixel so we are ready for the next iteration
        currentPixel += lastElementHeight;

        // Maintain the map of the heights
        virtualScrollItemPixelHeightMap.push(lastElementHeight);
    }

    // Set the totalheight initally (We assume all the items are visible to start with)
    virtualScrollerTotalHeight.value = virtualScrollItemStartPixelmap.at(-1) ?? currentPixel;
};

/**
 * End scroll on the dropdown menu
 * This is used for virtual scrolling
 * @param scrollEvent The scroll event
 */
const dropdownMenuScrollEnd = (scrollEvent: Event) => {
    // We are not in virtualscroller mode so just exit
    if (props.virtualScrollerMaxItems === undefined) {
        return;
    }
    if (!virtualScrollerScrollDiv.value) {
        return;
    }
    const target = scrollEvent.target;
    if (!target || !(target instanceof HTMLElement)) {
        return;
    }
    const scrollTop = target.scrollTop;

    // Find the first item that is below the scrollTop
    let newStartIndex = virtualScrollItemStartPixelmap.findIndex((pixelStart) => pixelStart > scrollTop) - 1;
    if (newStartIndex < 0) {
        newStartIndex = 0;
    }

    virtualScrollStartIndex.value = newStartIndex;
    virtualScrollEndIndex.value = Math.min(newStartIndex + props.virtualScrollerMaxItems, props.dropdownItems?.length ?? 0);

    // We set the translation to the start pixel of the new start index. This gives the smoothest user experience
    virtualScrollerScrollTransitionInPixels.value = virtualScrollItemStartPixelmap[newStartIndex] ?? scrollTop;
    // An alternative is to set it to scrollTop, but if we do that, the first item always starts pixel perfect at the top of where we have scrolled to and stuff "jumps around"
    // Setting it to the items start pixel as above allows us to half scroll an item into view, which is more consistent with how scrolling usually works.
    // virtualScrollerScrollTransitionInPixels.value = scrollTop;
};
</script>
<template>
    <div
        class="eq-dropdown dropdown d-flex"
        :class="classes"
        ref="dropdownelement"
    >
        <slot name="trigger">
            <!-- The triggering button (if not in split mode) -->
            <ButtonComp
                :disabled="props.disabled"
                :icon="icon"
                :icon-position="iconPosition"
                :button-type="buttonType"
                class="flex-grow-1 text-start"
                :class="[$attrs.class, props.showArrow ? 'dropdown-toggle' : '']"
                :data-bs-auto-close="autoClose"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                textposition="left"
                ref="dropdownTriggerElement"
                :loadstate="loadstate"
                v-if="!splitAction"
            >
                <template v-if="translationkey">{{ $tm(translationkey) }}</template>
                <span
                    v-if="showItemCountBadge"
                    class="badge bg-primary text-white rounded-pill ms-3"
                    >{{ computedDropdowItemsCount }}</span
                >
                <slot name="closedContent"></slot>
            </ButtonComp>

            <!-- Button and trigger if we are in split mode -->
            <template v-if="splitAction">
                <!-- The trigger button uses the data from the splitAction -->
                <ButtonComp
                    :icon="splitAction.props.icon ?? icon"
                    :icon-position="splitAction.props.iconPosition ?? iconPosition"
                    :button-type="splitAction.props.buttonType ?? buttonType"
                    class="flex-grow-1 text-start"
                    :class="[$attrs.class, splitAction.class]"
                    :data-bs-auto-close="autoClose"
                    textposition="left"
                    @click="splitAction?.action"
                    :disabled="props.disabled ?? splitAction?.props.disabled"
                    :loadstate="loadstate"
                >
                    <template v-if="splitAction.translationkey">{{ $tm(unref(splitAction.translationkey)) }}</template>
                </ButtonComp>
                <!-- the comp can be used with or without dropdownitems, so we only show this if there is items -->
                <template v-if="dropdownItems && dropdownItems?.length > 0">
                    <!-- Since the main button is used for an action, we need a secondary button to open the dropdown -->
                    <div class="vr"></div>
                    <ButtonComp
                        class="dropdown-toggle dropdown-toggle-split no-focus"
                        data-bs-toggle="dropdown"
                        :button-type="buttonType"
                        aria-expanded="false"
                        ref="dropdownTriggerElement"
                    >
                        <span class="visually-hidden"></span>
                    </ButtonComp>
                </template>
            </template>
        </slot>
        <!-- This slot is still here because typeahead uses it, but DO NOT USE IT in the future. This will be removed -->
        <slot name="menu">
            <ul
                class="dropdown-menu"
                :class="[
                    props.columns !== undefined && props.columns > 1 ? `dropdown-menu-columns cols-${props.columns ?? 1}` : '',
                    // If we have an actionGroup we have to show the overflow
                    // This means we cant have a long menu with a submenu on the same level.
                    // We do it this way since we cant set overflow-x and overflow-y to different values.
                    // See https://developer.mozilla.org/en-US/docs/Web/CSS/overflow#description
                    dropdownItems?.some((t) => t instanceof DropdownItemMenu) ? 'overflow-visible' : 'overflow-auto',
                ]"
                :style="{
                    minWidth: `${dropdownelement?.offsetWidth ?? 0}px`,
                    maxHeight: 'calc(75% - 32px)',
                }"
                ref="dropdownMenuUl"
                @scrollend="dropdownMenuScrollEnd"
            >
                <div :style="virtualScrollerTotalHeight !== null ? `min-height: ${virtualScrollerTotalHeight}px` : ''">
                    <div
                        :style="{
                            transform: `translateY(${virtualScrollerScrollTransitionInPixels}px)`,
                        }"
                        ref="virtualScrollerScrollDiv"
                    >
                        <template v-if="!dropdownItems?.length">
                            <slot name="emptyDropdown">
                                <li
                                    class="display-5 muted text-center py-4"
                                    v-if="emptytext"
                                >
                                    <IconComp
                                        icon="fa-info-circle"
                                        class="text-info"
                                    />
                                    <span v-html="$tm(emptytext)"></span>
                                </li>
                            </slot>
                        </template>
                        <template v-if="dropdownItems?.length">
                            <template
                                v-for="dropdownItem in visibleDropdownItems"
                                :key="dropdownItem.uniqueLoopKey"
                            >
                                <!-- if its a group we do an inner loop for the group items -->
                                <template v-if="dropdownItem instanceof DropdownItemGroup">
                                    <span class="dropdown-group">
                                        <template
                                            v-for="innerDropdownItem in dropdownItem.items"
                                            :key="innerDropdownItem.uniqueLoopKey"
                                        >
                                            <li
                                                v-if="!isDropDownItemHidden(innerDropdownItem)"
                                                :class="
                                                    dropdownItemsThatShouldNOTHaveDropdownItemClass.some(
                                                        (t) => innerDropdownItem instanceof t,
                                                    )
                                                        ? ''
                                                        : 'dropdown-item'
                                                "
                                            >
                                                <DropdownCompItemRenderer
                                                    :dropdown-item="innerDropdownItem"
                                                    class="dropdown-item dropend pe-1 me-0"
                                                    :class="[
                                                        innerDropdownItem instanceof DropdownItemSeparator ? 'dropdown-separator' : '',
                                                    ]"
                                                />
                                            </li>
                                        </template>
                                    </span>
                                </template>
                                <!-- Usual items -->
                                <template v-else>
                                    <li
                                        v-if="!isDropDownItemHidden(dropdownItem)"
                                        :class="
                                            dropdownItemsThatShouldNOTHaveDropdownItemClass.some((t) => dropdownItem instanceof t) ? '' : ''
                                        "
                                    >
                                        <DropdownCompItemRenderer
                                            :dropdown-item="dropdownItem"
                                            class="dropdown-item dropend pe-1 me-0"
                                            :class="[dropdownItem instanceof DropdownItemSeparator ? 'dropdown-separator' : '']"
                                        />
                                    </li>
                                </template>
                            </template>
                        </template>
                    </div>
                </div>
            </ul>
        </slot>
    </div>
</template>
